import jwt_decode from "jwt-decode";
import { UserTypes } from "enum/UserTypes";
import { AppResponse } from "../models/Response";
import { AuthService } from "./AuthService";

const STORAGE_KEY = "shopifyOnboardingSession";

export function getSignedInUserRole(): string | null {
  const token = AuthService.getToken();
  if (!token) {
    return null;
  }
  try {
    const data: { user_role?: string; role?: string } = jwt_decode(token);
    return data.user_role || data.role || null;
  } catch {
    return null;
  }
}

export function isSignedInZuluCustomer(): boolean {
  const role = getSignedInUserRole();
  return (
    role === UserTypes.BUSINESS_CUSTOMER || role === UserTypes.PERSONAL_CUSTOMER
  );
}

export function saveShopifyOnboardingSession(session: string): void {
  if (session) {
    sessionStorage.setItem(STORAGE_KEY, session);
  }
}

export function readShopifyOnboardingSession(): string {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("shopifySession") || params.get("session") || "";
  if (fromQuery) {
    saveShopifyOnboardingSession(fromQuery);
  }
  return fromQuery || sessionStorage.getItem(STORAGE_KEY) || "";
}

export function clearShopifyOnboardingSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function shopifySignInPath(session?: string): string {
  const value = session || readShopifyOnboardingSession();
  return value
    ? `/signin?shopifySession=${encodeURIComponent(value)}`
    : "/signin";
}

export function shopifySignUpPath(session?: string): string {
  const value = session || readShopifyOnboardingSession();
  return value
    ? `/signup?shopifySession=${encodeURIComponent(value)}`
    : "/signup";
}

export function shopifyOnboardingPath(session?: string, shop?: string): string {
  const params = new URLSearchParams();
  const value = session || readShopifyOnboardingSession();
  if (value) {
    params.set("session", value);
  }
  if (shop) {
    params.set("shop", shop);
  }
  const query = params.toString();
  return query
    ? `/integrations/shopify/onboarding?${query}`
    : "/integrations/shopify/onboarding";
}

export function shopifyDashboardPath(session?: string, shop?: string): string {
  const params = new URLSearchParams();
  const value = session || readShopifyOnboardingSession();
  if (value) {
    params.set("session", value);
  }
  if (shop) {
    params.set("shop", shop);
  }
  const query = params.toString();
  return query
    ? `/integrations/shopify/dashboard?${query}`
    : "/integrations/shopify/dashboard";
}

export async function completeShopifyAccountLink(): Promise<{
  linked: boolean;
  error?: string;
}> {
  const session = readShopifyOnboardingSession();
  if (!session || !isSignedInZuluCustomer()) {
    return { linked: false };
  }
  const res = (await AuthService.linkShopifyStore(session)) as AppResponse<{
    alreadyLinked?: boolean;
    ordersReassigned?: number;
  }>;
  if (res.success) {
    return { linked: true };
  }
  if (res.errorCode === "FORBIDDEN" || res.errorCode === "UNAUTHORIZED") {
    return { linked: false };
  }
  return { linked: false, error: res.error };
}
